import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  DatePicker,
  Spinner,
  Tab,
  Tabs
} from '@nextui-org/react';
import { parseAbsoluteToLocal } from '@internationalized/date';
import { apiFetch } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import FoundersReactivations from './FoundersReactivations';

// Ventana de la oferta Founders. El valor por defecto viene compilado en la
// app (renderer/config/foundersWindow.js); aquí se edita el override en D1
// que el worker aplica al instante (checkout + availability) y la app
// Electron recoge al arrancar / refrescar.

function formatIso(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// ISO UTC → ZonedDateTime en la zona horaria local (para el DatePicker).
function isoToZoned(iso) {
  if (!iso || isNaN(Date.parse(iso))) return null;
  return parseAbsoluteToLocal(new Date(iso).toISOString());
}

export default function Founders() {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold">Founders</h1>
        <p className="text-sm text-default-500">
          Ventana global de la oferta y campañas de reactivación por lotes.
        </p>
      </div>

      <Tabs aria-label="Secciones de Founders" variant="underlined">
        <Tab key="window" title="Ventana global">
          <FoundersWindowPanel />
        </Tab>
        <Tab key="reactivations" title="Reactivaciones">
          <FoundersReactivations />
        </Tab>
      </Tabs>
    </div>
  );
}

function FoundersWindowPanel() {
  const { canWrite } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [endValue, setEndValue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/backoffice/founders/window');
      setData(res);
      setEndValue(isoToZoned(res?.effective?.offerEndIso));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function handleSave() {
    if (!endValue) {
      setToast({ type: 'error', msg: 'Fecha de cierre no válida.' });
      return;
    }
    setSaving(true);
    setToast(null);
    try {
      const res = await apiFetch('/api/backoffice/founders/window', {
        method: 'PUT',
        body: { offerEndIso: endValue.toDate().toISOString() }
      });
      setData(res);
      setEndValue(isoToZoned(res?.effective?.offerEndIso));
      setToast({ type: 'ok', msg: `Fecha de cierre actualizada a ${formatIso(res?.effective?.offerEndIso)}.` });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!confirm('¿Quitar el override y volver a la fecha por defecto de la app?')) return;
    setSaving(true);
    setToast(null);
    try {
      const res = await apiFetch('/api/backoffice/founders/window', { method: 'DELETE' });
      setData(res);
      setEndValue(isoToZoned(res?.effective?.offerEndIso));
      setToast({ type: 'ok', msg: 'Override eliminado — vuelve a aplicar la fecha por defecto.' });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  const eff = data?.effective || {};
  const defaults = data?.defaults || {};

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-default-500">
          Ventana de la oferta Founders Edition para todo el mundo — la fecha de
          cierre se puede ampliar aquí sin sacar release.
        </p>
        <Button size="sm" variant="flat" onPress={reload} isLoading={loading}>
          Refrescar
        </Button>
      </div>

      {toast && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            toast.type === 'ok'
              ? 'bg-success-50 text-success-700'
              : 'bg-danger-50 text-danger-700'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </div>
      )}

      {loading && !data ? (
        <Spinner className="mt-8 self-center" />
      ) : data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex items-center justify-between gap-2 pb-0">
              <span className="text-base font-semibold">Estado actual</span>
              {data.closed ? (
                <Chip size="sm" color="danger" variant="flat">Cerrada</Chip>
              ) : data.notStarted ? (
                <Chip size="sm" color="default" variant="flat">Aún no abierta</Chip>
              ) : (
                <Chip size="sm" color="success" variant="flat">
                  Abierta · {data.daysRemaining} días restantes
                </Chip>
              )}
            </CardHeader>
            <CardBody className="gap-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wide text-default-400">Apertura</div>
                <div>{formatIso(eff.offerStartIso)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-default-400">Cierre efectivo</div>
                <div className="font-medium">{formatIso(eff.offerEndIso)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-default-400">Origen</div>
                <div className="flex items-center gap-2">
                  {data.hasOverride ? (
                    <>
                      <Chip size="sm" color="warning" variant="flat">Override backoffice</Chip>
                      <span className="text-default-500">
                        por defecto sería {formatIso(defaults.offerEndIso)}
                      </span>
                    </>
                  ) : (
                    <Chip size="sm" color="default" variant="flat">Valor por defecto de la app</Chip>
                  )}
                </div>
              </div>
              {data.hasOverride && data.updatedAt && (
                <div className="text-xs text-default-400">
                  Último cambio: {formatIso(new Date(data.updatedAt).toISOString())}
                  {data.updatedBy ? ` · uid ${data.updatedBy}` : ''}
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="pb-0">
              <span className="text-base font-semibold">Editar fecha de cierre</span>
            </CardHeader>
            <CardBody className="gap-3">
              <DatePicker
                label="Cierre de la oferta (tu hora local)"
                granularity="minute"
                showMonthAndYearPickers
                hideTimeZone
                value={endValue}
                onChange={setEndValue}
                isDisabled={!canWrite || saving}
              />
              <p className="text-xs text-default-500">
                Se guarda en UTC. El worker lo aplica en ~1 minuto (checkout y
                disponibilidad); la app lo recoge al arrancar y actualiza la
                cuenta atrás en caliente.
              </p>
              <div className="flex gap-2">
                <Button
                  color="primary"
                  onPress={handleSave}
                  isLoading={saving}
                  isDisabled={!canWrite}
                >
                  Guardar
                </Button>
                {data.hasOverride && (
                  <Button
                    variant="flat"
                    color="danger"
                    onPress={handleReset}
                    isDisabled={!canWrite || saving}
                  >
                    Quitar override
                  </Button>
                )}
              </div>
              {!canWrite && (
                <p className="text-xs text-warning-600">
                  Tu rol es de solo lectura — solo un admin completo puede editar.
                </p>
              )}
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
